import React, { useState, useEffect } from 'react';
import { 
  FileText, Upload, Trash2, Download, Pin, Search, FolderOpen, BookOpen, 
  Shield, ClipboardList, Megaphone, Eye, Clock, Filter, Grid, List,
  File, Image, Video, Music, Archive, Presentation, Table, Star, Bookmark,
  ChevronRight, Plus, MoreVertical, ExternalLink, Copy, Share2, Folder,
  HardDrive, Calendar, User, Tag, TrendingUp, Sparkles, Zap, RefreshCw,
  X, Check, AlertCircle, Info, Link2, FileCode, FileSpreadsheet, FilePlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'training', label: 'Training', icon: BookOpen, color: 'blue', gradient: 'from-blue-500 to-cyan-500', desc: 'Learning materials' },
  { id: 'policy', label: 'Policies', icon: Shield, color: 'purple', gradient: 'from-purple-500 to-pink-500', desc: 'Company policies' },
  { id: 'guidelines', label: 'Guidelines', icon: ClipboardList, color: 'emerald', gradient: 'from-emerald-500 to-green-500', desc: 'Procedures & SOPs' },
  { id: 'forms', label: 'Forms', icon: FileText, color: 'orange', gradient: 'from-orange-500 to-amber-500', desc: 'Templates & forms' },
  { id: 'announcements', label: 'Updates', icon: Megaphone, color: 'cyan', gradient: 'from-cyan-500 to-blue-500', desc: 'Latest updates' }
];

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'red', bg: 'bg-red-500/20' },
  doc: { icon: FileText, color: 'blue', bg: 'bg-blue-500/20' },
  docx: { icon: FileText, color: 'blue', bg: 'bg-blue-500/20' },
  xls: { icon: FileSpreadsheet, color: 'green', bg: 'bg-green-500/20' },
  xlsx: { icon: FileSpreadsheet, color: 'green', bg: 'bg-green-500/20' },
  ppt: { icon: Presentation, color: 'orange', bg: 'bg-orange-500/20' },
  pptx: { icon: Presentation, color: 'orange', bg: 'bg-orange-500/20' },
  jpg: { icon: Image, color: 'pink', bg: 'bg-pink-500/20' },
  jpeg: { icon: Image, color: 'pink', bg: 'bg-pink-500/20' },
  png: { icon: Image, color: 'pink', bg: 'bg-pink-500/20' },
  gif: { icon: Image, color: 'purple', bg: 'bg-purple-500/20' },
  mp4: { icon: Video, color: 'red', bg: 'bg-red-500/20' },
  mp3: { icon: Music, color: 'purple', bg: 'bg-purple-500/20' },
  zip: { icon: Archive, color: 'amber', bg: 'bg-amber-500/20' },
  txt: { icon: FileText, color: 'slate', bg: 'bg-slate-500/20' },
  csv: { icon: Table, color: 'green', bg: 'bg-green-500/20' },
  js: { icon: FileCode, color: 'yellow', bg: 'bg-yellow-500/20' },
  json: { icon: FileCode, color: 'amber', bg: 'bg-amber-500/20' }
};

const StaffResourcesPage = ({ getAuthHeaders, currentUser }) => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [bookmarkedResources, setBookmarkedResources] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'training',
    is_pinned: false,
    file: null,
    tags: ''
  });

  const isAdmin = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);

  useEffect(() => {
    fetchResources();
    // Load bookmarks and recently viewed from localStorage
    const savedBookmarks = localStorage.getItem('resource_bookmarks');
    if (savedBookmarks) setBookmarkedResources(JSON.parse(savedBookmarks));
    const savedRecent = localStorage.getItem('resource_recent');
    if (savedRecent) setRecentlyViewed(JSON.parse(savedRecent));
  }, []);

  useEffect(() => {
    filterResources();
  }, [resources, searchQuery, selectedCategory, sortBy]);

  const fetchResources = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/resources/documents`, { headers });
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = [...resources];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.tags?.toLowerCase().includes(query)
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }
    
    // Sort
    filtered.sort((a, b) => {
      // Pinned always first
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name':
          return a.title.localeCompare(b.title);
        case 'popular':
          return (b.views || 0) - (a.views || 0);
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    setFilteredResources(filtered);
  };

  const handleUpload = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('category', formData.category);
      submitData.append('is_pinned', formData.is_pinned);
      submitData.append('tags', formData.tags);
      if (formData.file) {
        submitData.append('file', formData.file);
      }

      await axios.post(`${BACKEND_URL}/api/resources/documents`, submitData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Resource uploaded successfully!');
      setShowUploadModal(false);
      setFormData({ title: '', description: '', category: 'training', is_pinned: false, file: null, tags: '' });
      fetchResources();
    } catch (error) {
      toast.error('Failed to upload resource');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/resources/documents/${resourceId}`, { headers });
      toast.success('Resource deleted');
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleBookmark = (resourceId) => {
    let newBookmarks;
    if (bookmarkedResources.includes(resourceId)) {
      newBookmarks = bookmarkedResources.filter(id => id !== resourceId);
      toast.success('Removed from bookmarks');
    } else {
      newBookmarks = [...bookmarkedResources, resourceId];
      toast.success('Added to bookmarks');
    }
    setBookmarkedResources(newBookmarks);
    localStorage.setItem('resource_bookmarks', JSON.stringify(newBookmarks));
  };

  const handleViewResource = (resource) => {
    setSelectedResource(resource);
    setShowPreviewModal(true);
    
    // Add to recently viewed
    const newRecent = [resource.id, ...recentlyViewed.filter(id => id !== resource.id)].slice(0, 10);
    setRecentlyViewed(newRecent);
    localStorage.setItem('resource_recent', JSON.stringify(newRecent));
  };

  const handleDownload = async (resource) => {
    const url = resource.file_url?.startsWith('http') ? resource.file_url : `${BACKEND_URL}${resource.file_url}`;
    const fallbackName = resource.file_name || `${resource.title || 'download'}.${resource.file_type || 'bin'}`;
    try {
      toast.info('Preparing download...');
      const headers = getAuthHeaders() || {};
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the browser has time to start the save
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
      toast.success(`Downloaded ${fallbackName}`);
    } catch (e) {
      toast.error('Download failed — opening file instead');
      window.open(url, '_blank');
    }
  };

  const handleCopyLink = (resource) => {
    const url = resource.file_url?.startsWith('http') ? resource.file_url : `${BACKEND_URL}${resource.file_url}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const getCategoryInfo = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];
  const getFileInfo = (fileType) => FILE_ICONS[fileType?.toLowerCase()] || { icon: File, color: 'slate', bg: 'bg-slate-500/20' };
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const pinnedResources = filteredResources.filter(r => r.is_pinned);
  const regularResources = filteredResources.filter(r => !r.is_pinned);
  const categoryStats = CATEGORIES.map(cat => ({
    ...cat,
    count: resources.filter(r => r.category === cat.id).length
  }));

  return (
    <div className="space-y-6" data-testid="staff-resources-page">
      {/* ═══ PREMIUM HERO ═══ */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] via-[#0b0a15] to-[#0a0d1a] p-6 lg:p-8">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em]">
              <FolderOpen className="w-3.5 h-3.5" style={{ color: '#00E5FF' }} />
              Knowledge vault
            </div>
            <h1 className="mt-2 text-3xl lg:text-4xl font-light text-white">
              Resource <span className="italic font-serif" style={{ color: '#00E5FF' }}>Centre</span>
            </h1>
            <p className="mt-2 text-white/40 text-sm">Training, policies, guidelines, forms and updates — all in one calm place.</p>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={fetchResources} variant="outline" className="bg-white/[0.02] border-white/10 text-white/80 hover:bg-white/[0.05] rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {isAdmin && (
              <Button onClick={() => setShowUploadModal(true)} className="bg-gradient-to-r from-[#00E5FF]/25 to-[#8A2BE2]/25 border border-[#00E5FF]/30 text-white hover:from-[#00E5FF]/35 hover:to-[#8A2BE2]/35 rounded-full">
                <FilePlus className="w-4 h-4 mr-2" />
                Upload resource
              </Button>
            )}
          </div>
        </div>

        {/* Category rail */}
        <div className="relative mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
          {categoryStats.map(cat => {
            const CatIcon = cat.icon;
            const active = selectedCategory === cat.id;
            // Map old color name to jewel hex
            const hexMap = { blue: '#00E5FF', purple: '#8A2BE2', emerald: '#10B981', orange: '#F97316', cyan: '#00E5FF' };
            const hex = hexMap[cat.color] || '#00E5FF';
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? 'all' : cat.id)}
                className="group relative overflow-hidden flex items-center gap-3 p-4 rounded-2xl bg-white/[0.02] border transition-all text-left"
                style={{ borderColor: active ? `${hex}55` : 'rgba(255,255,255,0.06)' }}
              >
                <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: `linear-gradient(to bottom, ${hex}, ${hex}00)` }} />
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${hex}18`, boxShadow: `0 0 20px ${hex}18` }}>
                  <CatIcon className="w-5 h-5" style={{ color: hex }} />
                </div>
                <div>
                  <p className="text-white text-lg font-light">{cat.count}</p>
                  <p className="text-white/40 text-xs">{cat.label}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Filters Bar */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search resources by title, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-white/[0.02] border border-white/[0.06] rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Filter Controls */}
        <div className="flex gap-2 flex-wrap">
          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:border-cyan-500/50"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">By Name</option>
            <option value="popular">Most Popular</option>
          </select>
          
          {/* View Mode */}
          <div className="flex gap-1 p-1 bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-cyan-500 text-white' : 'text-white/40 hover:text-white'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-cyan-500 text-white' : 'text-white/40 hover:text-white'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          
          {/* Clear Filter */}
          {selectedCategory !== 'all' && (
            <Button onClick={() => setSelectedCategory('all')} variant="outline" className="border-white/[0.08] text-white/70">
              <X className="w-4 h-4 mr-1" />
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {/* Pinned Section */}
      {pinnedResources.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Pin className="w-4 h-4 text-amber-400" />
            <h2 className="text-white font-semibold">Pinned Resources</h2>
            <Badge className="bg-amber-500/20 text-amber-400 border-0">{pinnedResources.length}</Badge>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pinnedResources.map(resource => (
              <ResourceCard 
                key={resource.id} 
                resource={resource} 
                onView={handleViewResource}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onBookmark={handleBookmark}
                onCopyLink={handleCopyLink}
                isBookmarked={bookmarkedResources.includes(resource.id)}
                isAdmin={isAdmin}
                getCategoryInfo={getCategoryInfo}
                getFileInfo={getFileInfo}
                formatDate={formatDate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main Resources */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50">Loading resources...</p>
          </div>
        </div>
      ) : regularResources.length === 0 && pinnedResources.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-white/[0.02] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-10 h-10 text-slate-600" />
          </div>
          <h3 className="text-white font-semibold text-lg mb-2">No resources found</h3>
          <p className="text-white/40 mb-6">Try adjusting your search or filters</p>
          {isAdmin && (
            <Button onClick={() => setShowUploadModal(true)} className="bg-cyan-500 hover:bg-cyan-600">
              <Upload className="w-4 h-4 mr-2" />
              Upload First Resource
            </Button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {regularResources.map(resource => (
            <ResourceCard 
              key={resource.id} 
              resource={resource} 
              onView={handleViewResource}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onBookmark={handleBookmark}
              onCopyLink={handleCopyLink}
              isBookmarked={bookmarkedResources.includes(resource.id)}
              isAdmin={isAdmin}
              getCategoryInfo={getCategoryInfo}
              getFileInfo={getFileInfo}
              formatDate={formatDate}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
          {regularResources.map((resource, idx) => (
            <ResourceListItem 
              key={resource.id} 
              resource={resource} 
              onView={handleViewResource}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onBookmark={handleBookmark}
              isBookmarked={bookmarkedResources.includes(resource.id)}
              isAdmin={isAdmin}
              getCategoryInfo={getCategoryInfo}
              getFileInfo={getFileInfo}
              formatDate={formatDate}
              isLast={idx === regularResources.length - 1}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus className="w-5 h-5 text-cyan-400" />
              Upload New Resource
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div>
              <label className="text-sm text-white/50 mb-2 block">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white focus:outline-none focus:border-cyan-500/50"
                placeholder="Resource title"
              />
            </div>
            
            <div>
              <label className="text-sm text-white/50 mb-2 block">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white resize-none focus:outline-none focus:border-cyan-500/50"
                placeholder="Describe this resource..."
              />
            </div>
            
            <div>
              <label className="text-sm text-white/50 mb-2 block">Category</label>
              <div className="grid grid-cols-5 gap-2">
                {CATEGORIES.map((cat) => {
                  const CatIcon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: cat.id })}
                      className={`p-3 rounded-xl flex flex-col items-center gap-2 transition-all border ${
                        formData.category === cat.id
                          ? `bg-gradient-to-br ${cat.gradient} border-transparent text-white shadow-lg`
                          : 'bg-white/[0.02] border-white/[0.08] text-white/50 hover:border-slate-600'
                      }`}
                    >
                      <CatIcon className="w-5 h-5" />
                      <span className="text-[10px] font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <label className="text-sm text-white/50 mb-2 block">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-white focus:outline-none focus:border-cyan-500/50"
                placeholder="e.g., onboarding, safety, 2024"
              />
            </div>
            
            <div>
              <label className="text-sm text-white/50 mb-2 block">File</label>
              <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                formData.file ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-white/[0.08] hover:border-slate-600'
              }`}>
                <input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {formData.file ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                        <File className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium">{formData.file.name}</p>
                        <p className="text-white/40 text-sm">{formatFileSize(formData.file.size)}</p>
                      </div>
                      <button 
                        type="button"
                        onClick={(e) => { e.preventDefault(); setFormData({ ...formData, file: null }); }}
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
                      <p className="text-white font-medium">Click to upload file</p>
                      <p className="text-white/40 text-sm mt-1">PDF, DOC, XLS, PPT, Images up to 50MB</p>
                    </>
                  )}
                </label>
              </div>
            </div>
            
            <label className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 cursor-pointer">
              <div
                onClick={() => setFormData({ ...formData, is_pinned: !formData.is_pinned })}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                  formData.is_pinned ? 'bg-amber-500 border-amber-500' : 'bg-transparent border-slate-600'
                }`}
              >
                {formData.is_pinned && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="flex-1">
                <span className="text-amber-400 font-medium flex items-center gap-2">
                  <Pin className="w-4 h-4" />
                  Pin to top
                </span>
                <p className="text-white/40 text-xs">Pinned resources appear first</p>
              </div>
            </label>
            
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowUploadModal(false)} variant="outline" className="flex-1 border-white/[0.08] text-white/70">
                Cancel
              </Button>
              <Button onClick={handleUpload} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600">
                <Upload className="w-4 h-4 mr-2" />
                Upload Resource
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-2xl">
          {selectedResource && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {(() => {
                    const catInfo = getCategoryInfo(selectedResource.category);
                    const CatIcon = catInfo.icon;
                    return (
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center`}>
                        <CatIcon className="w-5 h-5 text-white" />
                      </div>
                    );
                  })()}
                  {selectedResource.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-white/70">{selectedResource.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  <Badge className={`bg-${getCategoryInfo(selectedResource.category).color}-500/20 text-${getCategoryInfo(selectedResource.category).color}-400`}>
                    {getCategoryInfo(selectedResource.category).label}
                  </Badge>
                  {selectedResource.tags?.split(',').map((tag, i) => (
                    <Badge key={i} className="bg-slate-700 text-white/70">{tag.trim()}</Badge>
                  ))}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-white/40">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(selectedResource.created_at)}</span>
                  <span className="flex items-center gap-1"><User className="w-4 h-4" />{selectedResource.uploaded_by || 'Admin'}</span>
                </div>
                
                {selectedResource.file_url && (
                  <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
                    <Button onClick={() => handleDownload(selectedResource)} className="flex-1 bg-cyan-500 hover:bg-cyan-600">
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                    <Button onClick={() => handleCopyLink(selectedResource)} variant="outline" className="border-white/[0.08] text-white/70">
                      <Link2 className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onView, onDownload, onDelete, onBookmark, onCopyLink, isBookmarked, isAdmin, getCategoryInfo, getFileInfo, formatDate }) => {
  const catInfo = getCategoryInfo(resource.category);
  const CatIcon = catInfo.icon;
  const fileInfo = getFileInfo(resource.file_type);
  const FileIcon = fileInfo.icon;

  return (
    <div className={`group bg-white/[0.02] border rounded-2xl overflow-hidden hover:border-slate-600 transition-all ${
      resource.is_pinned ? 'border-amber-500/30 ring-1 ring-amber-500/20' : 'border-white/[0.06]'
    }`}>
      {/* Category Header */}
      <div className={`h-2 bg-gradient-to-r ${catInfo.gradient}`} />
      
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center shadow-lg`}>
            <CatIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex items-center gap-1">
            {resource.is_pinned && <Pin className="w-4 h-4 text-amber-400" />}
            <button
              onClick={() => onBookmark(resource.id)}
              className={`p-1.5 rounded-lg transition-all ${
                isBookmarked ? 'text-cyan-400 bg-cyan-500/20' : 'text-white/40 hover:text-white hover:bg-white/[0.02]'
              }`}
            >
              <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-cyan-400' : ''}`} />
            </button>
          </div>
        </div>
        
        <h3 className="text-white font-semibold mb-2 line-clamp-1">{resource.title}</h3>
        <p className="text-white/40 text-sm mb-4 line-clamp-2">{resource.description}</p>
        
        <div className="flex items-center justify-between mb-4">
          <Badge className={`bg-${catInfo.color}-500/20 text-${catInfo.color}-400 border-0`}>
            {catInfo.label}
          </Badge>
          <span className="text-slate-600 text-xs flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(resource.created_at)}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onView(resource)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/[0.02] text-white/70 hover:bg-slate-700 transition-all text-sm"
          >
            <Eye className="w-4 h-4" />
            View
          </button>
          {resource.file_url && (
            <button
              onClick={() => onDownload(resource)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-all text-sm"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => onDelete(resource.id)}
              className="p-2.5 rounded-xl bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Resource List Item Component
const ResourceListItem = ({ resource, onView, onDownload, onDelete, onBookmark, isBookmarked, isAdmin, getCategoryInfo, getFileInfo, formatDate, isLast }) => {
  const catInfo = getCategoryInfo(resource.category);
  const CatIcon = catInfo.icon;

  return (
    <div className={`flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors ${!isLast ? 'border-b border-white/[0.06]' : ''}`}>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center flex-shrink-0`}>
        <CatIcon className="w-6 h-6 text-white" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-white font-medium truncate">{resource.title}</h3>
          {resource.is_pinned && <Pin className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
        </div>
        <p className="text-white/40 text-sm truncate">{resource.description}</p>
      </div>
      
      <div className="flex items-center gap-3 flex-shrink-0">
        <Badge className={`bg-${catInfo.color}-500/20 text-${catInfo.color}-400 border-0 hidden sm:flex`}>
          {catInfo.label}
        </Badge>
        <span className="text-slate-600 text-xs hidden md:block">{formatDate(resource.created_at)}</span>
        
        <button
          onClick={() => onBookmark(resource.id)}
          className={`p-2 rounded-lg transition-all ${isBookmarked ? 'text-cyan-400 bg-cyan-500/20' : 'text-white/40 hover:text-white hover:bg-white/[0.02]'}`}
        >
          <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-cyan-400' : ''}`} />
        </button>
        
        <button onClick={() => onView(resource)} className="p-2 rounded-lg bg-white/[0.02] text-white/70 hover:bg-slate-700">
          <Eye className="w-4 h-4" />
        </button>
        
        {resource.file_url && (
          <button onClick={() => onDownload(resource)} className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30">
            <Download className="w-4 h-4" />
          </button>
        )}
        
        {isAdmin && (
          <button onClick={() => onDelete(resource.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StaffResourcesPage;
